import React from 'react'
import { getCustomTheme } from 'front-end/src/components/chakraThemeExtension'
import { apolloClientPublic } from 'front-end/src/graphql/apolloClient'
import { brandColors } from 'shared/basicPalette'
import { generatePalette, isColorDark } from 'shared/colorUtils'
import { useOrgColorsQuery } from './useOrgColors.codegen'
import { ChakraProvider } from '@chakra-ui/react'

export type colorsExtensionType = {
  navbarBackground: string
  navbarLink: string
  buttonPrimary: ReturnType<typeof generatePalette>
  linkText: string
}

type LooopThemeType = ReturnType<typeof useInitLooopTheme>

const LooopTheme = React.createContext<LooopThemeType>({
  loadingOrgColors: true,
  refetchOrgColors: () => Promise.resolve(),
  theme: getCustomTheme()
})

const useInitLooopTheme = () => {
  const { data, refetch, loading } = useOrgColorsQuery({
    fetchPolicy: 'cache-first',
    client: apolloClientPublic
  })

  return React.useMemo(() => {
    let extendBrandColors: colorsExtensionType = {} as colorsExtensionType

    if (!data)
      return {
        loadingOrgColors: false,
        refetchOrgColors: () => {},
        theme: getCustomTheme()
      }

    if (data?.organisation?.styles?.colors) {
      const { navbarBackground, navbarLink, buttonPrimary, linkText } =
        data.organisation.styles?.colors

      if (navbarBackground) {
        extendBrandColors.navbarBackground = navbarBackground
        // set correct default for text in navbar
        if (!navbarLink && isColorDark(navbarBackground)) {
          extendBrandColors.navbarLink = brandColors.white
        }
      }
      if (navbarLink) {
        extendBrandColors.navbarLink = navbarLink
      }

      if (buttonPrimary) {
        extendBrandColors.buttonPrimary = generatePalette(buttonPrimary, 4)
      }

      if (linkText) {
        extendBrandColors.linkText = linkText
      }
    }

    return {
      loadingOrgColors: loading,
      refetchOrgColors: refetch,
      theme: getCustomTheme(extendBrandColors)
    }
  }, [data, loading])
}

export const WithLooopThemeContext = ({ children }) => {
  const ctx = useInitLooopTheme()
  return (
    <LooopTheme.Provider value={ctx}>
      <ChakraProvider theme={ctx.theme} resetCSS={false}>
        {children}
      </ChakraProvider>
    </LooopTheme.Provider>
  )
}

export const useLooopTheme = () => {
  return React.useContext(LooopTheme)
}
